<template>
  <div class="score-distribution">
    <el-card shadow="never">
      <el-form inline>
        <el-form-item label="年级">
          <el-select v-model="filters.grade" placeholder="选择年级" style="width: 120px" @change="fetchData">
              <el-option v-for="g in availableGrades" :key="g" :label="g" :value="g" />
          </el-select>
        </el-form-item>
        <el-form-item label="科目">
          <el-select v-model="filters.subject" placeholder="选择科目" style="width: 120px" @change="fetchData">
            <el-option label="总分" value="总分" />
            <el-option v-for="s in subjects" :key="s" :label="s" :value="s" />
          </el-select>
        </el-form-item>
        <el-form-item label="分段跨度">
          <el-select v-model="filters.binSize" style="width: 100px" @change="fetchData">
            <el-option label="5分" :value="5" />
            <el-option label="10分" :value="10" />
            <el-option label="20分" :value="20" />
            <el-option label="50分" :value="50" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :icon="DataAnalysis" @click="fetchData" :loading="loading" :disabled="!semesterId || !examType || !filters.grade">
            分析
          </el-button>
        </el-form-item>
      </el-form>

      <div v-if="loading" style="padding: 40px; text-align: center;">
        <el-skeleton :rows="10" animated />
      </div>
      <el-empty v-else-if="!hasData" description="暂无成绩分段数据" />
      <div v-else class="chart-container">
        <v-chart class="chart" :option="chartOption" autoresize />
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, defineProps } from 'vue'
import VChart from 'vue-echarts'
import '@/utils/echarts'
import { DataAnalysis } from '@element-plus/icons-vue'
import { gradeAnalysisApi } from '@/api'
import { ElMessage } from 'element-plus'

const props = defineProps({
  semesterId: { type: Number, required: false },
  examType: { type: String, required: false },
  availableGrades: { type: Array, default: () => [] }
})

const loading = ref(false)
const subjects = ['语文', '数学', '英语', '物理', '化学', '生物', '政治', '历史', '地理']

const filters = reactive({
  grade: props.availableGrades[0] || '',
  subject: '总分',
  binSize: 10
})

const distData = ref(null)

const hasData = computed(() => distData.value && distData.value.bins && distData.value.bins.length > 0)

const chartOption = computed(() => {
  if (!hasData.value) return {}
  
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: distData.value.bins,
      axisTick: { alignWithLabel: true }
    },
    yAxis: {
      type: 'value',
      name: '人数'
    },
    series: [
      {
        name: '人数',
        type: 'bar',
        barWidth: '60%',
        data: distData.value.data,
        itemStyle: {
          color: '#4f46e5',
          borderRadius: [4, 4, 0, 0]
        },
        label: {
          show: true,
          position: 'top'
        }
      }
    ]
  }
})

const fetchData = async () => {
  if (!props.semesterId || !props.examType || !filters.grade) return
  
  loading.value = true
  try {
    distData.value = await gradeAnalysisApi.getScoreDistribution(
      props.semesterId, 
      props.examType, 
      filters.grade, 
      filters.subject, 
      filters.binSize
    )
    if (!hasData.value) {
      ElMessage.warning('该条件下无数据')
    }
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

watch(() => [props.semesterId, props.examType], () => {
  if (props.semesterId && props.examType) {
    fetchData()
  }
})
</script>

<style scoped>
.chart-container {
  height: 500px;
  width: 100%;
  margin-top: 20px;
}
.chart {
  width: 100%;
  height: 100%;
}
</style>
